import { useEffect } from 'react';
import { Box, Typography, TextField } from '@mui/material';

interface AgentPersonalizationProps {
  personalization: any;
  onPersonalizationChange: (personalization: any) => void;
  onValidationChange: (isValid: boolean) => void;
}

const AgentPersonalization = ({
  personalization,
  onPersonalizationChange,
  onValidationChange,
}: AgentPersonalizationProps) => {
  useEffect(() => {
    // Check if required fields are filled
    const isValid = !!(
      personalization?.greeting &&
      personalization?.identity &&
      personalization?.job
    );
    onValidationChange(isValid);
  }, [personalization, onValidationChange]);

  const handleInputChange = (field: string, value: string) => {
    onPersonalizationChange({
      ...personalization,
      [field]: value,
    });
  };

  return (
    <Box
      sx={{ maxWidth: '1400px', width: '100%', margin: '0 auto', py: 3, px: 4 }}
    >
      <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
        {/* Heading */}
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            color: 'custom.textBlue',
            mb: 1,
          }}
        >
          Personalize Your Agent
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            color: 'lightGray.text',
            mb: 3,
          }}
        >
          Make your agent sound unique and aligned with your brand
        </Typography>

        {/* Form */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '8px',
            p: 4,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Greeting */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Greeting Message *
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                How should your agent greet customers?
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., Hello! Welcome to our service. How can I help you today?"
                value={personalization?.greeting || ''}
                onChange={e => handleInputChange('greeting', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: 0,
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                    '&:hover fieldset': {
                      borderColor: '#DCE0E7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'none',
                  },
                }}
              />
            </Box>

            {/* Identity */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Identity *
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Who is your agent? (Name, role, or persona)
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., I'm Alex, your virtual assistant from ABC Company"
                value={personalization?.identity || ''}
                onChange={e => handleInputChange('identity', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: 0,
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                    '&:hover fieldset': {
                      borderColor: '#DCE0E7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                  },
                }}
              />
            </Box>

            {/* Job/Purpose */}
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Job Description *
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                What is your agent's main purpose?
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., I help customers with product inquiries, order tracking, and general support questions"
                value={personalization?.job || ''}
                onChange={e => handleInputChange('job', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: 0,
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                    '&:hover fieldset': {
                      borderColor: '#DCE0E7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'none',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Preview box */}
        {personalization?.greeting && personalization?.identity && (
          <Box
            sx={{
              mt: 3,
              bgcolor: 'secondary.main',
              border: '1px solid',
              borderColor: 'lightGray.main',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: 'custom.textBlue',
                mb: 1,
              }}
            >
              Preview
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                color: 'custom.textBlue',
                fontStyle: 'italic',
              }}
            >
              "{personalization.greeting} {personalization.identity}"
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AgentPersonalization;
